import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { RealtimeProvider } from '../lib/realtime.tsx';
import { useMessageSound } from '../lib/useMessageSound.ts';
import { useTabAlert } from '../lib/useTabAlert.ts';
import { useTheme } from '../lib/theme.ts';
import { Avatar } from './ui.tsx';
import {
  LogOutIcon,
  MessageSquareIcon,
  MonitorIcon,
  MoonIcon,
  InfoIcon,
  SettingsIcon,
  SunIcon,
  BoxIcon,
} from './icons.tsx';

/**
 * App shell: an icon rail on desktop that becomes a bottom bar on phones,
 * where a fixed side rail would eat scarce horizontal space.
 *
 * Split in two so the socket sits above everything that listens to it — the
 * rail's badge and the tab alert both want events, and so does the Chats
 * screen underneath the Outlet.
 */
export function AppLayout(): React.JSX.Element {
  return (
    <RealtimeProvider>
      <AppShell />
    </RealtimeProvider>
  );
}

function AppShell(): React.JSX.Element {
  const { me, logout, isManager } = useAuth();
  const { theme, setTheme } = useTheme(me?.preferences.theme as 'light' | 'dark' | 'system');
  const navigate = useNavigate();

  // Both need the socket, which is why they live here rather than on Chats:
  // the whole point of either is to reach somebody who is not on Chats.
  useTabAlert();
  useMessageSound();

  /**
   * How many people are waiting, wherever the agent happens to be.
   *
   * Pushed now: the socket is open on every screen and invalidates this key,
   * so the badge moves the moment a message lands. The interval stays as a
   * long backstop for a connection that has quietly died — a badge that is
   * wrong until the next event is worse than one that is a minute stale.
   *
   * `refetchOnWindowFocus` is off globally and turned back on here alone,
   * because the interval does not tick while the browser tab is hidden and a
   * tab left open over lunch would come back showing a number from before it.
   */
  const unread = useQuery({
    queryKey: ['unread'],
    queryFn: () => api.get<{ conversations: number }>('/sessions/unread'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // A stale number for a moment beats a spinner in a nav rail; keeping the
    // last value means the badge never blinks out while it refetches.
    placeholderData: (previous) => previous,
  });

  const waiting = unread.data?.conversations ?? 0;

  const links = [
    { to: '/chats', label: 'Chats', icon: MessageSquareIcon, visible: true, badge: waiting },
    { to: '/management', label: 'Management', icon: MonitorIcon, visible: isManager, badge: 0 },
    { to: '/inventory', label: 'Inventory', icon: BoxIcon, visible: true, badge: 0 },
    { to: '/settings', label: 'Settings', icon: SettingsIcon, visible: true, badge: 0 },
    // Last, and visible to everyone: an agent has as much use for it as an
    // owner, and more, because nobody walked them through the setup.
    { to: '/help', label: 'How this works', icon: InfoIcon, visible: true, badge: 0 },
  ].filter((link) => link.visible);

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <nav
        aria-label="Main"
        className="flex shrink-0 items-center justify-around border-t border-slate-200 bg-white px-2 py-1.5
                   md:w-16 md:flex-col md:justify-start md:gap-1 md:border-t-0 md:border-r md:py-4
                   dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="hidden md:mb-4 md:block">
          <img src="/logo-96.png" alt="LeadGab" width={36} height={36} className="h-9 w-9" />
        </div>

        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            title={link.label}
            className={({ isActive }) =>
              clsx(
                'flex flex-1 flex-col items-center gap-0.5 rounded-lg px-3 py-2 text-[11px] font-medium transition md:flex-none md:gap-0 md:p-2.5',
                isActive
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
              )
            }
          >
            {/*
              The badge hangs off the icon rather than sitting beside it: the
              rail is icon-width on desktop and the label is a screen-reader
              one, so there is no row to lay it out in. `relative` here rather
              than on the link, which the active-state classes already own.
            */}
            <span className="relative">
              <link.icon className="h-5 w-5" />

              {/*
                aria-hidden because the sr-only line below says the same thing
                in words. Left readable, a screen reader would announce the
                count twice — and past a hundred, twice with two different
                numbers, since only the pill is capped.
              */}
              {link.badge > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute -top-1.5 -right-2 min-w-4 rounded-full bg-brand-600 px-1 text-[10px] leading-4 font-semibold text-white tabular-nums"
                >
                  {link.badge > 99 ? '99+' : link.badge}
                </span>
              ) : null}
            </span>

            <span className="md:sr-only">{link.label}</span>

            {link.badge > 0 ? (
              <span className="sr-only">
                {link.badge === 1 ? '1 conversation waiting' : `${String(link.badge)} conversations waiting`}
              </span>
            ) : null}
          </NavLink>
        ))}

        <div className="hidden flex-1 md:block" />

        <button
          type="button"
          onClick={() => {
            setTheme(theme === 'dark' ? 'light' : 'dark');
          }}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="hidden rounded-lg p-2.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 md:block dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          {theme === 'dark' ? <MoonIcon className="h-5 w-5" /> : <SunIcon className="h-5 w-5" />}
          <span className="sr-only">Toggle theme</span>
        </button>

        <div className="hidden md:mt-1 md:block">
          <Avatar src={me?.user.avatar} name={me?.user.displayName ?? '?'} size={32} />
        </div>

        <button
          type="button"
          onClick={() => void handleLogout()}
          title="Log out"
          className="hidden rounded-lg p-2.5 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600 md:block dark:text-slate-400 dark:hover:bg-rose-950 dark:hover:text-rose-400"
        >
          <LogOutIcon className="h-5 w-5" />
          <span className="sr-only">Log out</span>
        </button>
      </nav>

      <main className="min-h-0 min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
